import React, { useState } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  Box,
  makeStyles,
  SelectProps,
  useMediaQuery,
} from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    "& input": {
      width: "100%",
    },
  },
  column: {
    display: "flex",
    flexDirection: "column",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  label: {
    fontSize: "0.775rem",
    fontWeight: 500,
  },
  labelRow: {
    width: "100%",
  },
  select: {
    width: "100%",
    marginTop: theme.spacing(0.75),
    padding: theme.spacing(0, 1),
    fontSize: "0.875rem", // text-sm
    "@media (max-width: 450px)": {
      marginTop: 0,
      fontSize: "0.775rem", // text-base
      padding: theme.spacing(0, 1),
      "& .MuiOutlinedInput-input": {
        padding: theme.spacing(1, 0.25),
      },
    },
    borderRadius: theme.shape.borderRadius * 3,
    "& .MuiSelect-root": {
      fontSize: "0.775rem",
    },
  },
  error: {
    color: theme.palette.error.main,
  },
  borderError: {
    "&:focus": {
      outline: "none",
      boxShadow: `0 0 0 2px ${theme.palette.error.main}`,
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.error.main,
    },
    "& fieldset": {
      borderColor: theme.palette.error.main,
    },
  },
  borderNormal: {
    border: `1px solid #e5e7eb;`, // ~ border-gray-300
    "&:focus": {
      outline: "none",
      border: `1px solid ${theme.palette.primary.light}`,
      boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.common.white,
    },
    "& fieldset": {
      borderColor: theme.palette.common.white,
    },
  },
  customItem: {
    fontSize: "0.775rem",
    color: "rgb(107 114 128)",
  },
  item: {
    minHeight: 0,
    "@media (max-width: 450px)": {
      padding: theme.spacing(0.5, 1),
    },
    padding: theme.spacing(0.75, 1),
  },

  customSelect: {
    width: "100%",
    marginTop: 0,
    padding: theme.spacing(1.5, 1),
    fontSize: "0.775rem", // text-sm
    borderRadius: theme.shape.borderRadius * 3,
    "& .MuiSelect-root": {
      fontSize: "0.775rem",
    },
    color: "rgb(107 114 128)",
    "@media (max-width: 450px)": {
      padding: theme.spacing(1, 0),
    },
  },
}));
export interface CustomSelectProps {
  /** Libellé du select (optionnel) */
  label?: string;
  /** Valeur actuelle */
  value?: string | number | null;
  /** Fonction de changement */
  setValue(name: string, value: string): void;
  /** Valeur de l'option par défaut si nécessaire (souvent gérée en externe) */
  defaultValue?: string;
  /** Placeholder affiché en option désactivée (optionnel) */
  placeholder?: string;
  /** Liste des options du select */
  options: Array<{
    id: string | number;
    label: string;
    display?: string;
  }>;
  isSubmitted?: boolean;
  displayError?: boolean;
  errorMessageId?: string;
  displayLabel?: boolean;
  isRow?: boolean;
  isCustom?: boolean;
  minWidth?: number;
  displayOtherValue?: boolean;
}

const CustomSelect: React.FC<CustomSelectProps & SelectProps> = ({
  label,
  value,
  placeholder,
  options,
  defaultValue,
  displayLabel,
  setValue,
  isRow,
  isCustom,
  minWidth,
  displayOtherValue,
  ...props
}) => {
  const classes = useStyles();
  const isMobile = useMediaQuery("(max-width: 450px)");
  const isSubmitted = !!props.isSubmitted;
  const displayError = !!props.displayError;
  const errorMessageId = props.errorMessageId
    ? props.errorMessageId
    : "error.notEmptyField";
  const hasError = (isSubmitted && !value) || (isSubmitted && displayError);
  const [values, setValues] = useState({
    select: "",
  });

  function handleChange(event: React.ChangeEvent<{ name?: any; value: any }>) {
    event.preventDefault();
    event.stopPropagation();
    setValues((oldValues) => ({
      ...oldValues,
      ["select"]: event.target.value,
    }));
    setValue(event.target.name, event.target.value);
  }
  return (
    <Box
      className={classes.root}
      style={{
        paddingBottom: isCustom ? 0 : 16,
        minWidth: minWidth ? minWidth : 0,
      }}
    >
      <Box className={isRow ? classes.row : classes.column}>
        {label && (
          <span
            className={classNames(
              classes.label,
              hasError && classes.error,
              isRow && classes.labelRow,
            )}
          >
            {label}
          </span>
        )}
        <FormControl fullWidth>
          <Select
            value={value}
            onChange={handleChange}
            defaultValue={defaultValue}
            variant="outlined"
            className={classNames(
              isCustom ? classes.customSelect : classes.select,
              hasError ? classes.borderError : classes.borderNormal,
            )}
            {...props}
            {...(displayLabel && { label })}
            renderValue={
              displayOtherValue
                ? (selectedValue) => {
                    const selectedOption = options?.find(
                      (option) => option.id === selectedValue,
                    );
                    // Si on trouve l'option, on affiche son 'display' s'il existe, sinon son 'label'
                    return (
                      <FormattedMessage
                        id={selectedOption?.display ?? selectedOption?.label}
                      />
                    );
                  }
                : undefined
            }
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              getContentAnchorEl: null,
              PaperProps: {
                style: {
                  marginTop: isMobile ? 10 : 12, // Ajustez éventuellement pour compenser un éventuel décalage
                },
              },
            }}
          >
            {placeholder && (
              <MenuItem value="" disabled>
                {placeholder}
              </MenuItem>
            )}
            {options.map((option) => (
              <MenuItem
                key={option.id}
                value={option.id}
                className={classNames(
                  classes.item,
                  isCustom && classes.customItem,
                )}
              >
                <FormattedMessage id={option.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {!!hasError && !value && (
        <span className={classes.error}>
          {<FormattedMessage id={errorMessageId} />}
        </span>
      )}
    </Box>
  );
};
export default CustomSelect;
